import { AxiosInstance } from 'axios'
import {
  FavoritePlacePayload,
  GetFavoritePlacesParams,
  PlaceShort,
  UpdatePlacePayload
} from 'types/places'
import { PLACES_ENDPOINTS } from 'api/ApiRoutes'
import { ID } from 'types/utility'

class PlacesApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getFavoritePlaces = async ({
    team_token_id,
    teamOnly = false,
    search,
    filter,
    filter_params,
    page = 1,
    limit = 30,
    archived,
    teamEndpoint
  }: GetFavoritePlacesParams) => {
    const typeQuery = team_token_id
      ? `type=${teamOnly ? 'team' : 'combined'}`
      : ''
    const teamQuery = team_token_id ? `team_token_id=${team_token_id}` : ''
    const filterQuery = ({
      filter,
      filter_params
    }: {
      filter: GetFavoritePlacesParams['filter']
      filter_params: GetFavoritePlacesParams['filter_params']
    }) => {
      if (filter === 'recently_added') return 'sort_by=created_at'
      if (filter === 'alphabetical') return 'sort_by=name'
      if (filter === 'closest') {
        return `latitude=${filter_params?.lat}&longitude=${filter_params?.lng}`
      }
      return ''
    }
    const query = `search=${search}&${typeQuery}&${teamQuery}&${filterQuery({
      filter,
      filter_params
    })}${archived === undefined ? '' : `&archived=${archived}`}`

    if (teamEndpoint && team_token_id) {
      const { data: teamData } = await this.axiosInstance.get<PlaceShort[]>(
        `${PLACES_ENDPOINTS.TEAM_PLACES(team_token_id)}?${query}&page=${page}&per_page=${limit}&paginate=by_page`
      )
      return teamData
    }

    const { data } = await this.axiosInstance.get<PlaceShort[]>(
      `${PLACES_ENDPOINTS.BASE}?${query}&page=${page}&per_page=${limit}&paginate=by_page`
    )
    return data
  }

  getFavoritePlace = async (place_token_id: ID, team_token_id?: ID) => {
    if (team_token_id) {
      const { data: teamData } = await this.axiosInstance.get<PlaceShort>(
        PLACES_ENDPOINTS.TEAM_PLACE(team_token_id, place_token_id)
      )
      return teamData
    }

    const { data } = await this.axiosInstance.get<PlaceShort>(
      PLACES_ENDPOINTS.PLACE(place_token_id)
    )
    return data
  }

  createFavoritePlace = async (
    payload: FavoritePlacePayload,
    team_token_id?: ID
  ) => {
    if (team_token_id) {
      const { data: teamData } = await this.axiosInstance.post(
        PLACES_ENDPOINTS.TEAM_PLACES(team_token_id),
        { place: payload }
      )
      return teamData
    }

    const { data } = await this.axiosInstance.post(
      PLACES_ENDPOINTS.BASE,
      payload
    )
    return data
  }

  updateFavoritePlace = async (
    payload: UpdatePlacePayload,
    team_token_id?: ID
  ) => {
    if (team_token_id) {
      const { data: teamData } = await this.axiosInstance.put(
        PLACES_ENDPOINTS.TEAM_PLACE(team_token_id, payload.token_id),
        { place: payload }
      )
      return teamData
    }
    const { data } = await this.axiosInstance.put(
      PLACES_ENDPOINTS.PLACE(payload.token_id),
      {
        ...payload
      }
    )
    return data
  }

  deleteFavoritePlace = async ({
    team_token_id,
    place_token_id
  }: {
    team_token_id?: ID
    place_token_id: ID
  }) => {
    if (team_token_id) {
      const { data: teamData } = await this.axiosInstance.delete<{
        result: 'success'
      }>(PLACES_ENDPOINTS.TEAM_PLACE(team_token_id, place_token_id))
      return teamData
    }
    const { data } = await this.axiosInstance.delete<{
      result: 'success'
    }>(PLACES_ENDPOINTS.PLACE(place_token_id))
    return data
  }

  activateFavoritePlace = async ({
    team_token_id,
    place_token_id
  }: {
    team_token_id: ID
    place_token_id: ID
  }) => {
    const d = await this.axiosInstance.put<PlaceShort>(
      PLACES_ENDPOINTS.ACTIVATE_TEAM_PLACE(team_token_id, place_token_id)
    )
    return d.data
  }

  updatePlaceClassification = async ({
    place_token_id,
    classification
  }: {
    place_token_id: ID
    classification: PlaceShort['classification']
  }) => {
    const { data } = await this.axiosInstance.patch<PlaceShort>(
      PLACES_ENDPOINTS.PLACE(place_token_id),
      { classification }
    )
    return data
  }

  importFavoritePlaces = async ({
    team_token_id,
    file
  }: {
    team_token_id: ID
    file: File
  }) => {
    const formData = new FormData()
    formData.append('file', file)
    const d = await this.axiosInstance.post<{
      result: 'success'
      token_id: ID
    }>(PLACES_ENDPOINTS.IMPORT_TEAM_PLACES(team_token_id), formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return d.data
  }
}

export default PlacesApi
